import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { find, get } from 'lodash';
import { PageTitle } from '../../PageTitle/PageTitle';
import { I18nReactContext } from '../../../contexts/I18nContext';
import { LocalTabs } from '../../../Utils/RoutingTabs/Tabs';
import { SignDocUsersReports } from '../SignDocUsersReports/SignDocUsersReports';
import { SurveyQuestionsResults } from '../QuestionnaireReports/SurveyQuestionsResults/SurveyQuestionsResults';
import { getSignDocReportAPI } from '../../../Services/api';
import useSignDocTypes from '../../../Hooks/useSignDocTypes';
import './SignDocResultsDetailPage.scss';
import { useUserAdminOrgs } from '../../Hooks/UseUserAdminOrgs';

/**
 * SignDoc Results Detail Page - displays user completion table for any SignDoc type
 * Generic component that works for courses, exams, surveys, documents, etc.
 * Conditionally shows tabs with question analytics for types that support it (SURVEY, EXAM)
 * Other types show only the users table without tabs
 *
 * @returns {JSX.Element} SignDoc results detail page component
 */
export const SignDocResultsDetailPage = () => {
    const { i18n } = useContext(I18nReactContext);
    const { signDocId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { getNestedProperty } = useSignDocTypes();
    const { adminOrganizationOptions } = useUserAdminOrgs({
        withAllOption: true,
        withCoursesAndSignDocs: false
    });

    const searchParams = new URLSearchParams(location.search);

    // Parse organizationIds from URL (always use organizationIds format)
    const organizationIds = useMemo(() => {
        const multipleOrgIds = searchParams.get('organizationIds');

        if (multipleOrgIds) {
            // Format: ?organizationIds=uuid1,uuid2,uuid3 or ?organizationIds=all
            return multipleOrgIds.split(',').filter(id => id.trim().length > 0);
        } else {
            // Default: all organizations
            return ['all'];
        }
    }, [location.search]);

    const [signDocData, setSignDocData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [organizationsNames, setOrganizationsNames] = useState(String(i18n.translate('courseReports.allOrganizations')));

    /**
     * Handles organization filter changes from the table
     * Updates the URL query parameters with the new organization selection
     * @param {Array<string>} selectedOrgIds - Array of selected organization IDs
     */
    const handleOrganizationFilterChange = (selectedOrgIds) => {
        const searchQuery = selectedOrgIds.length > 0 ? `organizationIds=${selectedOrgIds.join(',')}` : 'organizationIds=all';
        navigate({
            pathname: location.pathname,
            search: searchQuery
        }, { replace: true });
    };

    // Organization names for the title display logic
    useEffect(() => {
        if (adminOrganizationOptions && adminOrganizationOptions.length > 0) {
            if (organizationIds.includes('all') || organizationIds.length === 0) {
                // All organizations or default
                setOrganizationsNames(String(i18n.translate('courseReports.allOrganizations')));
            } else if (organizationIds.length === 1) {
                // Single specific organization
                const organization = find(adminOrganizationOptions, option => option.value === organizationIds[0]);
                setOrganizationsNames(get(organization, 'text', ''));
            } else {
                // Multiple specific organizations
                const orgNames = organizationIds
                    .map(orgId => {
                        const org = find(adminOrganizationOptions, option => option.value === orgId);
                        return get(org, 'text', '');
                    })
                    .filter(name => name.length > 0);

                if (orgNames.length <= 3) {
                    // Show individual names if 3 or fewer
                    setOrganizationsNames(orgNames.join(', '));
                } else {
                    // Show count if more than 3
                    const displayedOrgNames = orgNames.slice(0, 3);
                    setOrganizationsNames(`${displayedOrgNames.join(', ')} ...`);
                }
            }
        }
    }, [adminOrganizationOptions, organizationIds, i18n]);

    /**
     * Fetches SignDoc report data from the API.
     * @param {Object} params
     * @param {Array<string>} params.orgIds - Organization IDs to scope stats to
     * @returns {Promise<object|null>} Extracted signDoc metadata or null
     */
    const fetchSignDocReport = async ({orgIds}) => {
        const signDoc = await getSignDocReportAPI({
            signDocId: signDocId,
            organizationIds: orgIds
        });
        if (!signDoc) return null;
        return {
            name: get(signDoc, 'docName', ''),
            totalCompleted: get(signDoc, 'reportData.totalCompleted', 0),
            totalAssigned: get(signDoc, 'reportData.totalAssigned', 0),
            completionRate: get(signDoc, 'reportData.progressPercentage', 0),
            type: get(signDoc, 'type', ''),
            questionsData: get(signDoc, 'questionsData', null),
            organizationSignDoc: get(signDoc, 'organizationSignDoc', []),
            isAnonymous: get(signDoc, 'reportData.isAnonymous', false),
            learningPathChildren: get(signDoc, 'learningPathChildren', null)
        };
    };

    // Load the header stats for the organizations currently filtered on.
    //
    // Two fetches are needed and they must stay in this order. The 'all' response is the only one
    // carrying the SignDoc's full organizationSignDoc list, which feeds the filter dropdown - a
    // scoped response only lists the organizations that were asked for. The stats, on the other
    // hand, must come from the scoped response, or the header counts every organization the admin
    // manages while the users table below shows only the filtered ones.
    //
    // `showLoader` is off when refreshing after a change made in the table below. This page renders
    // nothing while loading, so raising the loader would unmount that table and reset it mid-change.
    // A failed refresh keeps the numbers already on screen, for the same reason.
    const loadReport = useCallback(async ({showLoader = true} = {}) => {
        if (!signDocId) return;

        try {
            if (showLoader) setIsLoading(true);

            const allOrgsData = await fetchSignDocReport({orgIds: ['all']});
            const isFilteredToSpecificOrgs = !organizationIds.includes('all') && organizationIds.length > 0;
            const scopedData = isFilteredToSpecificOrgs
                ? await fetchSignDocReport({orgIds: organizationIds})
                : allOrgsData;

            setSignDocData(scopedData && {
                ...scopedData,
                organizationSignDoc: get(allOrgsData, 'organizationSignDoc', [])
            });
        } catch (error) {
            console.error('Error fetching SignDoc detail data:', error);
            if (showLoader) setSignDocData(null);
        } finally {
            if (showLoader) setIsLoading(false);
        }
    }, [signDocId, organizationIds]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    /**
     * Re-reads the header stats after the table changed a user's completion.
     * Marking someone complete moves the numerator, so the header must not stay behind.
     * @returns {void}
     */
    const handleReportDataChanged = useCallback(() => {
        loadReport({showLoader: false});
    }, [loadReport]);

    /**
     * Creates tab configurations if the SignDoc type supports question analytics
     * @returns {Array|null} Array of tab configurations or null if no tabs needed
     */
    const memoizedTabs = useMemo(() => {
        if (!signDocData) return null;

        const signDocType = get(signDocData, 'type', '');
        const usersTabLabel = i18n.translate(
            getNestedProperty({
                type: signDocType,
                section: 'questionnaireResults',
                property: 'usersTabLabelKey',
                defaultValue: 'surveyResults.tabs.users'
            })
        );

        const tabs = [{
            label: usersTabLabel,
            value: 'users',
            content: (
                <SignDocUsersReports
                    signDocId={signDocId}
                    organizationIds={organizationIds}
                    adminOrganizationOptions={adminOrganizationOptions}
                    signDocData={signDocData}
                    onOrganizationFilterChange={handleOrganizationFilterChange}
                    onReportDataChanged={handleReportDataChanged}
                />
            )
        }];
      

        const hasQuestionAnalytics = getNestedProperty({
            type: signDocType,
            section: 'questionnaireResults',
            property: 'hasQuestionAnalytics',
            defaultValue: false
        });
        // If no question analytics, don't create tabs
        if (hasQuestionAnalytics) {
        const resultsTabLabel = i18n.translate(
            getNestedProperty({
                type: signDocType,
                section: 'questionnaireResults',
                property: 'resultsTabLabelKey',
                defaultValue: 'surveyResults.tabs.results'
            })
        );

        tabs.push({
                label: resultsTabLabel,
                value: 'results',
                content: (
                    <SurveyQuestionsResults
                        questionsData={get(signDocData, 'questionsData', [])}
                        surveyData={signDocData}
                    />
                )
            });
        }

        return tabs;
    }, [i18n, signDocData, signDocId, organizationIds, getNestedProperty, handleOrganizationFilterChange, handleReportDataChanged]);

    // Show "not found" message if SignDoc doesn't exist
    if (!signDocData && !isLoading) {
        return (
            <div className="SignDocResultsDetailPage MenuPage">
                <PageTitle
                    title={i18n.translate('courseReports.title')}
                    subTitle={i18n.translate('courseReports.notFound')}
                    subTitleBold=""
                    customComponent={null}
                    noLogo={false}
                    size="small"
                />
            </div>
        );
    }

    return !isLoading && signDocData ? (
        <div className="SignDocResultsDetailPage MenuPage">
            <PageTitle
                title={get(signDocData, 'name', i18n.translate('courseReports.defaultTitle'))}
                subTitle={i18n.translate('courseReports.subtitle', {
                    context: {
                        completed: get(signDocData, 'totalCompleted', 0),
                        assigned: get(signDocData, 'totalAssigned', 0),
                        rate: parseInt(get(signDocData, 'completionRate', 0).toFixed(1))
                    }
                })}
                subTitleBold={`${organizationIds.includes('all') ? '' : i18n.translate('courseReports.organization')} - ${organizationsNames}`}
                customComponent={null}
                noLogo={false}
                size="small"
            />

            <div className="menuInnerPage">
                <div className="tabsContainer">
                    <LocalTabs tabs={memoizedTabs} />
                </div>
            </div>
        </div>
    ) : null;
};
